import type { ReactNode, Dispatch, SetStateAction } from "react";
import { createContext, useEffect, useState } from "react";

enum GlobalContextUserRole {
  CLERK = "clerk",
  PATIENT = "patient",
  PRACTITIONER = "practitioner",
  ROLELESS = "roleless",
  UNAUTHENTICATED = "unauthenticated",
  ORG_MANAGER = "organization_manager",
}
interface GlobalContextType {
  adminImpersonating?: boolean;
  hidePhiData?: boolean;
  country: string;
  role: GlobalContextUserRole;
  setRole: Dispatch<SetStateAction<GlobalContextUserRole>>;
  isStoreOwner?: boolean;
  isEmerson?: boolean;
  e2e: boolean;
  iterableApiKey?: string;
  rudderstackJsApiWriteKey?: string;
  rudderstackDataPlaneUrl?: string;
  // Rupa practitioner details from migration token
  rupaPractitionerDetails?: {
    email?: string;
    dispensaryName?: string;
    practitionerFirstName?: string;
    practitionerLastName?: string;
    practitionerType?: {
      id: string;
      description: string;
      createdAt: string;
      updatedAt: string;
      code: string;
      core: boolean;
    };
    /** True when store has multiple practitioners (unauthenticated Rupa migration only). Undefined/false = solo copy. */
    isMultiPractitioner?: boolean;
  };
}

const GlobalContextDefault: GlobalContextType = {
  adminImpersonating: false,
  hidePhiData: false,
  country: "United States",
  role: GlobalContextUserRole.UNAUTHENTICATED,
  isStoreOwner: false,
  isEmerson: false,
  e2e: false,
  setRole: () => null,
};

const GlobalContext = createContext<GlobalContextType>(GlobalContextDefault);

interface Props {
  adminImpersonating?: boolean;
  hidePhiData?: boolean;
  country: string;
  role: GlobalContextUserRole;
  isStoreOwner?: boolean;
  isEmerson?: boolean;
  e2e: boolean;
  children: ReactNode;
  iterableApiKey?: string;
  rudderstackJsApiWriteKey?: string;
  rudderstackDataPlaneUrl?: string;
  // Rupa practitioner details from migration token
  rupaPractitionerDetails?: {
    email?: string;
    dispensaryName?: string;
    practitionerFirstName?: string;
    practitionerLastName?: string;
    practitionerType?: {
      id: string;
      description: string;
      createdAt: string;
      updatedAt: string;
      code: string;
      core: boolean;
    };
    isMultiPractitioner?: boolean;
  };
}

const GlobalContextProvider = ({
  children,
  adminImpersonating,
  hidePhiData,
  role,
  country,
  isEmerson,
  e2e,
  iterableApiKey,
  rudderstackJsApiWriteKey,
  rudderstackDataPlaneUrl,
  isStoreOwner,
  rupaPractitionerDetails,
}: Props) => {
  const [stateRole, setRole] = useState(role);

  useEffect(() => {
    if (stateRole !== role) {
      setRole(role);
    }
  }, [stateRole, setRole, role]);

  return (
    <GlobalContext
      value={{
        country,
        adminImpersonating,
        hidePhiData,
        role: stateRole,
        setRole,
        isStoreOwner,
        isEmerson,
        e2e,
        iterableApiKey,
        rudderstackJsApiWriteKey,
        rudderstackDataPlaneUrl,
        rupaPractitionerDetails,
      }}
    >
      {children}
    </GlobalContext>
  );
};

export { GlobalContext, GlobalContextUserRole, GlobalContextProvider, GlobalContextDefault };
export type { GlobalContextType };
